import { Controller } from "@hotwired/stimulus"
import { chooseZipAction, isZip, uploadResponse } from "../zip_upload"

export default class extends Controller {
  static targets = ["dropzone", "form", "fileInput", "queue", "itemTemplate"]
  static values = {
    url: String
  }

  connect() {
    this.uploadQueue = []
  }

  // Trigger hidden file input
  triggerFileInput(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  // Handle file selection
  handleFiles(event) {
    const files = Array.from(event.target.files)
    this.addFilesToQueue(files)
  }

  // Drag and drop handlers
  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-gray-400", "bg-gray-100")
    this.dropzoneTarget.classList.remove("border-gray-300")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-gray-400", "bg-gray-100")
    this.dropzoneTarget.classList.add("border-gray-300")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-gray-400", "bg-gray-100")
    this.dropzoneTarget.classList.add("border-gray-300")

    const files = Array.from(event.dataTransfer.files)
    this.addFilesToQueue(files)
  }

  // Add files to upload queue and start uploading
  async addFilesToQueue(files) {
    if (files.length === 0) return

    // Show queue container
    this.queueTarget.classList.remove("hidden")

    for (const file of files) {
      // Validate file type
      if (!this.isValidFileType(file)) {
        this.showError(`${file.name}: Invalid file type (allowed: PNG, JPG, GIF, WebP, MP4, MOV, PDF, TXT, ZIP)`)
        continue
      }

      // Validate file size (500MB)
      if (file.size > 500 * 1024 * 1024) {
        this.showError(`${file.name}: File too large (max 500MB)`)
        continue
      }

      const zipAction = await chooseZipAction(file)
      if (!zipAction) continue

      const uploadId = this.generateId()
      const upload = {
        id: uploadId,
        file: file,
        zipAction,
        progress: 0,
        status: "pending"
      }

      this.uploadQueue.push(upload)
      this.renderUploadItem(upload)

      // Start upload immediately
      this.uploadFile(upload).catch(error => console.error("Upload failed:", error))
    }
  }

  // Upload a single file
  async uploadFile(upload) {
    return new Promise((resolve, reject) => {
      upload.status = "uploading"
      this.updateUploadProgress(upload, 0)

      const formData = new FormData()
      formData.append("asset[original_file]", upload.file)
      formData.append("zip_action", upload.zipAction)

      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total)
          this.updateUploadProgress(upload, percentCompleted)
        }
      })

      // Handle successful upload
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          upload.status = "complete"
          this.updateUploadProgress(upload, 100)

          const response = uploadResponse(xhr)
          if (response.import_url) {
            const link = document.createElement("a")
            link.href = response.import_url
            link.className = "text-sm underline"
            link.textContent = "View import status"
            this.queueTarget.querySelector(`[data-upload-id="${upload.id}"]`).appendChild(link)
          }

          // Asset will appear via Turbo broadcast - no reload needed!
          resolve()
        } else {
          upload.status = "error"
          this.updateUploadError(upload, uploadResponse(xhr).error || "Upload failed")
          reject(new Error("Upload failed"))
        }
      })

      // Handle errors
      xhr.addEventListener("error", () => {
        upload.status = "error"
        this.updateUploadError(upload, "Network error")
        reject(new Error("Network error"))
      })

      xhr.open("POST", this.urlValue)
      xhr.setRequestHeader("X-CSRF-Token", this.getCsrfToken())
      xhr.setRequestHeader("Accept", "application/json")
      xhr.send(formData)
    })
  }

  // Render upload item in queue
  renderUploadItem(upload) {
    const template = this.itemTemplateTarget.content.cloneNode(true)
    const item = template.querySelector("[data-upload-item]")

    item.dataset.uploadId = upload.id
    item.querySelector("[data-filename]").textContent = upload.file.name
    item.querySelector("[data-filesize]").textContent = this.formatFileSize(upload.file.size)

    this.queueTarget.appendChild(template)
  }

  // Update upload progress
  updateUploadProgress(upload, percent) {
    const item = this.queueTarget.querySelector(`[data-upload-id="${upload.id}"]`)
    if (!item) return

    const progressBar = item.querySelector("[data-progress]")
    const progressText = item.querySelector("[data-progress-text]")
    const statusBadge = item.querySelector("[data-status-badge]")

    // Update progress bar width
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }

    if (upload.status === "complete") {
      // Update to success state
      if (progressBar) {
        progressBar.classList.remove("bg-blue-600")
        progressBar.classList.add("bg-green-600")
      }

      if (statusBadge) {
        statusBadge.classList.remove("badge-info")
        statusBadge.classList.add("badge-success")
        statusBadge.innerHTML = `
          <i class="fadr fa-check-circle text-xs"></i>
          <span>${upload.zipAction === "unzip" ? "Queued" : "Complete"}</span>
        `
      }

      item.classList.add("border-green-200")

      // Auto-dismiss after 20 seconds
      if (upload.zipAction !== "unzip") {
        setTimeout(() => this.dismissItemById(upload.id), 20000)
      }
    } else {
      // Only update progress text if not at 100% (before statusBadge.innerHTML replacement)
      if (progressText) {
        progressText.textContent = percent === 100 ? "Importing…" : `${percent}%`
      }
    }
  }

  // Update upload error
  updateUploadError(upload, message) {
    const item = this.queueTarget.querySelector(`[data-upload-id="${upload.id}"]`)
    if (!item) return

    const progressBar = item.querySelector("[data-progress]")
    const statusBadge = item.querySelector("[data-status-badge]")
    const dismissButton = item.querySelector("[data-dismiss-button]")

    // Update to error state
    progressBar.classList.remove("bg-blue-600")
    progressBar.classList.add("bg-red-600")

    statusBadge.classList.remove("badge-info")
    statusBadge.classList.add("badge-error")
    statusBadge.innerHTML = `
      <i class="fadr fa-exclamation-circle text-xs"></i>
      <span>Failed</span>
    `

    item.classList.remove("border-gray-200")
    item.classList.add("border-red-300", "bg-red-50")

    const error = document.createElement("p")
    error.className = "text-sm text-red-700"
    error.textContent = message
    item.appendChild(error)

    // Show dismiss button for failed uploads
    dismissButton.classList.remove("hidden")
  }

  // Validate file type
  isValidFileType(file) {
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/gif",
      "image/webp",
      "video/mp4",
      "video/quicktime",
      "application/pdf",
      "text/plain"
    ]
    return isZip(file) || validTypes.includes(file.type)
  }

  // Format file size
  formatFileSize(bytes) {
    if (bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 10) / 10 + " " + sizes[i]
  }

  // Generate unique ID
  generateId() {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  // Get CSRF token
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Show error message
  showError(message) {
    alert(message) // TODO: Replace with toast notification
  }

  // Prevent form submission
  submitForm(event) {
    event.preventDefault()
  }

  // Dismiss an upload item (called by dismiss button)
  dismissItem(event) {
    const item = event.target.closest("[data-upload-item]")
    if (!item) return

    // Fade out and remove
    item.style.opacity = "0"
    setTimeout(() => {
      item.remove()
      this.checkIfQueueEmpty()
    }, 300)
  }

  // Dismiss an upload item by ID
  dismissItemById(uploadId) {
    const item = this.queueTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (!item) return

    // Fade out and remove
    item.style.opacity = "0"
    setTimeout(() => {
      item.remove()
      this.checkIfQueueEmpty()
    }, 300)
  }

  // Check if queue is empty and hide container
  checkIfQueueEmpty() {
    const items = this.queueTarget.querySelectorAll("[data-upload-item]")
    if (items.length === 0) {
      this.queueTarget.classList.add("hidden")
    }
  }
}
